// ════════════════════════════════════════════════════════════════════════════
//  pages/Fiscal/MDFe.tsx
//  Listagem paginada de MDF-e com emissão, nova transmissão, encerramento,
//  cancelamento, DAMDFE e XML
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import configuracoesService from '@/features/configuracoes/services/configuracoesService';
import { FileText, XCircle, Search, ChevronLeft, ChevronRight, RefreshCw, Plus, Printer, CheckCircle, Trash2, Download, Send } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { useMDFe } from '@/features/fiscal/transporte/hooks/useMDFe';
import { mdfeService } from '@/features/fiscal/transporte/services/mdfeService';
import { statusDoMDFe } from '@/features/fiscal/transporte/types';
import type { StatusMDFe, MDFeResumoDto, EmitirMDFeRequest, EncerrarMDFeRequest } from '@/features/fiscal/transporte/types';
import { useVeiculosCombo } from '@/features/veiculos/hooks/useVeiculos';
import { useCondutoresCombo, useReboquesCombo } from '@/features/transporte/hooks/useTransporte';
import { CitySearchCombobox } from '@/components/CitySearchCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const PAGE_SIZES = [10, 20, 50] as const;

const UFS_BR = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

const statusConfig: Record<StatusMDFe, { label: string; className: string }> = {
  autorizado: { label: 'Autorizado',   className: 'bg-green-500/10 text-green-600'  },
  encerrado:  { label: 'Encerrado',    className: 'bg-blue-500/10 text-blue-600'    },
  cancelado:  { label: 'Cancelado',    className: 'bg-red-500/10 text-red-600'      },
  rejeitado:  { label: 'Não autorizado', className: 'bg-orange-500/10 text-orange-600'},
  digitacao:  { label: 'Em digitação', className: 'bg-yellow-500/10 text-yellow-600'},
};

const chaveResumida = (chave: string | null) =>
  chave ? `...${chave.slice(-12)}` : '—';

// Dia de HOJE no relógio do usuário. toISOString() dá o dia em UTC: depois das 21h (Brasília)
// já é "amanhã", e a SEFAZ recusa data no futuro.
const hojeLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formVazio = (): EmitirMDFeRequest => ({
  serie: '1', dtEmissao: hojeLocal(),
  veiculoId: undefined, reboqueId: undefined, condutoresIds: [],
  documentosVinculados: [],
  ufInicio: '', ufFim: '', ufsPercurso: [],
  seguroResponsavel: 1, seguradora: '', apolice: '',
});

const encVazio = (): EncerrarMDFeRequest => ({ dtEncerramento: hojeLocal(), ufEncerramento: '', municipioEncerramento: '' });

// Radix Select não aceita value vazio: sentinela para "sem reboque".
const SEM_REBOQUE = '__none__';

const MDFe = () => {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [busca,         setBusca]         = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  // Abre nos manifestos de hoje, mais recentes primeiro.
  const periodo   = usePeriodo('hoje', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero'>('emissao', true, () => setPagina(1));

  // A busca vai ao servidor: espera a digitação parar.
  const [buscaEnviada, setBuscaEnviada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscaEnviada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  // Dialogs
  const [emitindoOpen,  setEmitindoOpen]  = useState(false);
  const [encerrando,   setEncerrando]    = useState<MDFeResumoDto | null>(null);
  const [cancelandoDoc, setCancelandoDoc] = useState<MDFeResumoDto | null>(null);

  // Emissão form
  const [form,           setForm]           = useState<EmitirMDFeRequest>(formVazio);
  const [ufsPercursoText,setUfsPercursoText]= useState('');
  const [novDocChave,    setNovDocChave]    = useState('');
  // Ambiente do manifesto: nasce no do emitente; o interruptor troca NESTE manifesto.
  // Emitente em homologação (ou sem resposta do serviço fiscal) não emite em produção.
  const [emitenteEmProducao, setEmitenteEmProducao] = useState(false);
  const [producao,           setProducao]           = useState(false);
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;

  // Encerramento (o município vai pelo código IBGE; o nome é só para mostrar)
  const [encForm,   setEncForm]   = useState<EncerrarMDFeRequest>(encVazio);
  const [encCidade, setEncCidade] = useState('');

  // Cancelamento
  const [justificativa, setJustificativa] = useState('');

  const { docs, total, totalPaginas, contagem, isLoading, isFetching, emitir, transmitir, encerrar, cancelar } =
    useMDFe(pagina, tamanhoPagina, {
      busca: buscaEnviada,
      situacao: statusFiltro,
      ...periodo.intervalo,
      ordenarPor: ordenacao.ordenarPor,
      decrescente: ordenacao.decrescente,
    });
  const { data: veiculos   = [] } = useVeiculosCombo();
  const { data: condutores = [] } = useCondutoresCombo();
  const { data: reboques   = [] } = useReboquesCombo();

  // Busca, situação e período já vêm filtrados do servidor.
  const docsFiltrados = docs;

  // Cartões: contagem do servidor para o período/busca inteiro (não só a página).
  const totalAutorizados = contagem?.autorizados    ?? 0;
  const totalEncerrados  = contagem?.encerrados     ?? 0;
  const totalCancelados  = contagem?.cancelados     ?? 0;
  const totalPendentes   = contagem?.naoAutorizados ?? 0;

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const setF = <K extends keyof EmitirMDFeRequest>(k: K, v: EmitirMDFeRequest[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const addDocumento = () => {
    const chave = novDocChave.replace(/[^0-9]/g, '');
    if (chave.length !== 44) { toast.error('A chave de acesso tem 44 dígitos.'); return; }
    // Posições 21–22 da chave são o modelo: 55 = NF-e. CT-e (57) ainda não é aceito no manifesto.
    if (chave.slice(20, 22) !== '55') { toast.error('Por enquanto o MDF-e aceita só NF-e (modelo 55).'); return; }
    if (form.documentosVinculados.some((d) => d.chave === chave)) { toast.error('Esta nota já está na lista.'); return; }
    setForm((prev) => ({
      ...prev,
      documentosVinculados: [...prev.documentosVinculados, { chave, tipo: 'NFe' }],
    }));
    setNovDocChave('');
  };

  const addCondutor = (id: number) =>
    setForm((prev) => prev.condutoresIds.includes(id) ? prev : { ...prev, condutoresIds: [...prev.condutoresIds, id] });

  const removeCondutor = (id: number) =>
    setForm((prev) => ({ ...prev, condutoresIds: prev.condutoresIds.filter((c) => c !== id) }));

  const removeDocumento = (idx: number) =>
    setForm((prev) => ({
      ...prev,
      documentosVinculados: prev.documentosVinculados.filter((_, i) => i !== idx),
    }));

  const abrirEmissao = () => {
    setForm(formVazio()); setUfsPercursoText(''); setNovDocChave('');
    setEmitenteEmProducao(false); setProducao(false);
    setEmitindoOpen(true);
    if (!empresaId) return;
    configuracoesService.getEmitenteFiscal(empresaId)
      .then((e) => {
        const emProducao = e.cadastrado && e.ambiente === 'producao';
        setEmitenteEmProducao(emProducao);
        setProducao(emProducao);
      })
      .catch(() => { /* sem resposta: fica em homologação */ });
  };

  const handleEmitir = async () => {
    const falta =
        !form.veiculoId                        ? 'Selecione o veículo.'
      : form.condutoresIds.length === 0        ? 'Inclua ao menos um condutor.'
      : form.documentosVinculados.length === 0 ? 'Inclua ao menos uma NF-e.'
      : !form.ufInicio || !form.ufFim          ? 'Informe a UF de início e a de fim.'
      : null;
    if (falta) { toast.error(falta); return; }

    const payload: EmitirMDFeRequest = {
      ...form,
      isAmbienteProducao: producao,
      ufsPercurso: ufsPercursoText.split(',').map((u) => u.trim().toUpperCase()).filter(Boolean),
    };
    try {
      await emitir.mutateAsync(payload);
      setEmitindoOpen(false);
    } catch {
      // O aviso já saiu pelo hook. Se o manifesto chegou a ser gravado, ele está na lista para
      // transmitir de novo; o formulário fica aberto para corrigir o que faltou.
    }
  };

  const baixar = (acao: Promise<void>) => acao.catch((e) => toast.error(getApiErrorMessage(e)));

  const handleEncerrar = () => {
    if (!encerrando) return;
    encerrar.mutate({ id: encerrando.id, req: encForm });
    setEncerrando(null);
  };

  const handleCancelar = () => {
    if (!cancelandoDoc || justificativa.trim().length < 15) return;
    cancelar.mutate({ id: cancelandoDoc.id, req: { justificativa } });
    setCancelandoDoc(null);
    setJustificativa('');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">MDF-e</h1>
          <p className="text-muted-foreground mt-1">Manifesto Eletrônico de Documentos Fiscais</p>
        </div>
        <Button className="gap-2" onClick={abrirEmissao}>
          <Plus className="h-4 w-4" />
          Emitir MDF-e
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Autorizados',  value: totalAutorizados, color: 'text-green-500' },
          { label: 'Encerrados',   value: totalEncerrados,  color: 'text-blue-500'  },
          { label: 'Cancelados',   value: totalCancelados,  color: 'text-red-500'   },
          { label: 'Não autorizados', value: totalPendentes, color: 'text-yellow-500'},
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <p className="text-xs text-muted-foreground mt-1">No período filtrado</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="whitespace-nowrap">Lista de MDF-e</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Número, chave ou veículo..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="autorizado">Autorizado</SelectItem>
                  <SelectItem value="encerrado">Encerrado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                  <SelectItem value="rejeitado">Não autorizado</SelectItem>
                  <SelectItem value="digitacao">Em digitação</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Número</CabecalhoOrdenavel>
                  <TableHead>Série</TableHead>
                  <TableHead>Chave</TableHead>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  <TableHead>UF Início → Fim</TableHead>
                  <TableHead>Veículo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : docsFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo === 'hoje' ? 'Nenhum MDF-e emitido hoje — mude o período para ver os anteriores'
                        : 'Nenhum MDF-e encontrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  docsFiltrados.map((doc) => {
                    const status = statusDoMDFe(doc);
                    const cfg    = statusConfig[status];
                    const temXml = !!doc.protocolo;
                    return (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">
                          {doc.numero > 0 ? String(doc.numero).padStart(6, '0') : '—'}
                          {!doc.isAmbienteProducao && <span className="ml-1 text-[10px] text-muted-foreground" title="Homologação — sem valor fiscal">HML</span>}
                        </TableCell>
                        <TableCell>{doc.serie ?? '—'}</TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground" title={doc.chave ?? undefined}>
                          {chaveResumida(doc.chave)}
                        </TableCell>
                        <TableCell>{new Date(doc.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{doc.ufInicio ?? '—'} → {doc.ufFim ?? '—'}</TableCell>
                        <TableCell>{doc.veiculoPlaca ?? '—'}</TableCell>
                        <TableCell>
                          <Badge className={cfg.className} title={doc.situacao ?? undefined}>{cfg.label}</Badge>
                          {(status === 'rejeitado' || status === 'digitacao') && doc.situacao && (
                            <p className="text-xs text-muted-foreground mt-1 max-w-[16rem]">{doc.situacao}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {(status === 'rejeitado' || status === 'digitacao') && (
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-primary"
                                disabled={transmitir.isPending}
                                onClick={() => transmitir.mutate(doc.id)}
                                title="Transmitir de novo (mantém o número)">
                                <Send className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {temXml && (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  onClick={() => baixar(mdfeService.abrirDamdfe(doc.id))}
                                  title="DAMDFE">
                                  <Printer className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  onClick={() => baixar(mdfeService.baixarXml(doc.id, doc.chave))}
                                  title="Baixar XML do MDF-e">
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                            {(status === 'encerrado' || status === 'cancelado') && (
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                                onClick={() => baixar(mdfeService.baixarXml(doc.id, doc.chave, status === 'cancelado' ? 'cancelamento' : 'encerramento'))}
                                title={status === 'cancelado' ? 'Baixar XML do cancelamento' : 'Baixar XML do encerramento'}>
                                <FileText className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {status === 'autorizado' && (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-blue-600 hover:text-blue-700"
                                  onClick={() => {
                                    setEncerrando(doc);
                                    setEncForm(encVazio());
                                    setEncCidade('');
                                  }}
                                  title="Encerrar MDF-e">
                                  <CheckCircle className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => { setCancelandoDoc(doc); setJustificativa(''); }}
                                  title="Cancelar MDF-e">
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={(v) => { setTamanhoPagina(Number(v)); setPagina(1); }}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)          page = i + 1;
                  else if (pagina <= 3)                 page = i + 1;
                  else if (pagina >= totalPaginas - 2)  page = totalPaginas - 4 + i;
                  else                                  page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog: Emitir MDF-e */}
      <Dialog open={emitindoOpen} onOpenChange={(o) => { if (!o) setEmitindoOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Emitir MDF-e</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Switch checked={producao} disabled={!emitenteEmProducao} onCheckedChange={setProducao} />
            <Label>
              Produção
              {!producao && (
                <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-500 text-xs">
                  HOMOLOGAÇÃO — teste, sem valor fiscal
                </Badge>
              )}
            </Label>
            {!emitenteEmProducao && (
              <span className="text-xs text-muted-foreground">O emitente está em homologação.</span>
            )}
          </div>
          <Tabs defaultValue="veiculo">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="veiculo">Veículo</TabsTrigger>
              <TabsTrigger value="documentos">Documentos</TabsTrigger>
              <TabsTrigger value="percurso">Percurso</TabsTrigger>
              <TabsTrigger value="seguro">Seguro</TabsTrigger>
            </TabsList>

            <TabsContent value="veiculo" className="space-y-3 pt-3">
              <div className="space-y-1">
                <Label>Veículo *</Label>
                <Select value={form.veiculoId ? String(form.veiculoId) : undefined}
                  onValueChange={(v) => setF('veiculoId', Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Selecione o veículo de tração" /></SelectTrigger>
                  <SelectContent>
                    {veiculos.map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>
                        {v.placa ?? 'sem placa'}{v.modelo ? ` — ${v.modelo}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Reboque (opcional)</Label>
                <Select value={form.reboqueId ? String(form.reboqueId) : SEM_REBOQUE}
                  onValueChange={(v) => setF('reboqueId', v === SEM_REBOQUE ? undefined : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_REBOQUE}>Sem reboque</SelectItem>
                    {reboques.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>{r.placa} — {r.uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Condutores *</Label>
                <Select key={form.condutoresIds.length} onValueChange={(v) => addCondutor(Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Incluir condutor" /></SelectTrigger>
                  <SelectContent>
                    {condutores.filter((c) => !form.condutoresIds.includes(c.id)).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.nome} — CPF {c.cpf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.condutoresIds.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">Nenhum condutor incluído</p>
                ) : (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {form.condutoresIds.map((id) => {
                      const c = condutores.find((x) => x.id === id);
                      return (
                        <Badge key={id} variant="secondary" className="gap-1">
                          {c?.nome ?? `Condutor ${id}`}
                          <button type="button" onClick={() => removeCondutor(id)} title="Remover condutor">
                            <XCircle className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="documentos" className="space-y-3 pt-3">
              <div className="flex gap-2">
                <Input value={novDocChave} onChange={(e) => setNovDocChave(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDocumento(); } }}
                  placeholder="Chave de acesso da NF-e (44 dígitos)" className="flex-1" />
                <Button type="button" variant="outline" onClick={addDocumento}>Adicionar</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Por enquanto o manifesto aceita só NF-e emitida pelo sistema; o município de descarga vem do endereço da nota.
              </p>
              {form.documentosVinculados.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum documento vinculado</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Chave</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.documentosVinculados.map((doc, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs font-mono" title={doc.chave}>...{doc.chave.slice(-20)}</TableCell>
                          <TableCell>{doc.tipo}</TableCell>
                          <TableCell>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive"
                              onClick={() => removeDocumento(idx)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="percurso" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Série *</Label>
                  <Input value={form.serie} onChange={(e) => setF('serie', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Data de Emissão *</Label>
                  <Input type="date" value={form.dtEmissao} onChange={(e) => setF('dtEmissao', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>UF Início *</Label>
                  <Select value={form.ufInicio} onValueChange={(v) => setF('ufInicio', v)}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {UFS_BR.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>UF Fim *</Label>
                  <Select value={form.ufFim} onValueChange={(v) => setF('ufFim', v)}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {UFS_BR.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>UFs de Percurso (separadas por vírgula)</Label>
                <Input value={ufsPercursoText} onChange={(e) => setUfsPercursoText(e.target.value)}
                  placeholder="Ex: SP, RJ, MG" />
              </div>
            </TabsContent>

            <TabsContent value="seguro" className="space-y-3 pt-3">
              <div className="space-y-1">
                <Label>Responsável pelo Seguro</Label>
                <Select value={String(form.seguroResponsavel)} onValueChange={(v) => setF('seguroResponsavel', Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 — Emitente do MDF-e</SelectItem>
                    <SelectItem value="2">2 — Contratante do Serviço</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Seguradora</Label>
                <Input value={form.seguradora} onChange={(e) => setF('seguradora', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Número da Apólice</Label>
                <Input value={form.apolice} onChange={(e) => setF('apolice', e.target.value)} />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setEmitindoOpen(false)}>Cancelar</Button>
            <Button onClick={handleEmitir} disabled={emitir.isPending}>
              {emitir.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Emitindo...</>
              ) : (
                <><FileText className="h-4 w-4 mr-2" />Emitir MDF-e</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Encerrar MDF-e */}
      <Dialog open={!!encerrando} onOpenChange={(o) => { if (!o) setEncerrando(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encerrar MDF-e {encerrando ? String(encerrando.numero).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Data de Encerramento *</Label>
              <Input type="date" value={encForm.dtEncerramento}
                onChange={(e) => setEncForm((prev) => ({ ...prev, dtEncerramento: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Município de Encerramento *</Label>
              <CitySearchCombobox
                value={encCidade}
                placeholder="Buscar o município onde a carga foi entregue..."
                onSelect={(c) => {
                  setEncCidade(c.uf ? `${c.nome} — ${c.uf}` : c.nome);
                  setEncForm((prev) => ({
                    ...prev,
                    municipioEncerramento: c.codigoIbge ? String(c.codigoIbge) : '',
                    ufEncerramento: c.uf ?? '',
                  }));
                }}
              />
              {encCidade && !encForm.municipioEncerramento && (
                <p className="text-xs text-destructive">Este município está sem código IBGE no cadastro de cidades.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEncerrando(null)}>Cancelar</Button>
            <Button onClick={handleEncerrar} disabled={encerrar.isPending || !encForm.ufEncerramento || !encForm.municipioEncerramento}>
              {encerrar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Encerrando...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" />Confirmar Encerramento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Cancelar MDF-e */}
      <Dialog open={!!cancelandoDoc} onOpenChange={(o) => { if (!o) setCancelandoDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar MDF-e {cancelandoDoc ? String(cancelandoDoc.numero).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Informe a justificativa do cancelamento (mínimo 15 caracteres). Esta ação será enviada à SEFAZ.
            </p>
            <div className="space-y-1">
              <Label>Justificativa *</Label>
              <Textarea rows={4} placeholder="Descreva o motivo do cancelamento..."
                value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
              <p className="text-xs text-muted-foreground text-right">{justificativa.length} / 255 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelandoDoc(null)}>Fechar</Button>
            <Button variant="destructive" onClick={handleCancelar}
              disabled={cancelar.isPending || justificativa.trim().length < 15}>
              {cancelar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" />Confirmar Cancelamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MDFe;
